import { createContext, use, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import {
  api,
  onAuthenticationLost,
  refreshAccessToken,
  setAccessToken,
} from "@/lib/api/client";
import type { Session, TokenPair } from "@/lib/api/types";
import { clearImageCache } from "@/lib/api/fileCache";
import { forgetSignIn, markTab, mayRestoreSession, rememberDevice } from "@/lib/auth/device";
import { answerOtherTabs, askOtherTabs } from "@/lib/auth/handshake";
import { recordActivity } from "@/lib/auth/idle";
import { IdleSignOut } from "@/lib/auth/IdleSignOut";

interface AuthContextValue {
  session: Session | null;
  /** True until the first refresh attempt settles, so guards do not flash. */
  initialising: boolean;
  /**
   * `rememberMe` is the tick box: on, the sign-in outlives the tab and any
   * tab on this device may pick it up; off, it goes when the tab does.
   */
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<Session>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<Session | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [initialising, setInitialising] = useState(true);

  const loadSession = useCallback(async (): Promise<Session | null> => {
    try {
      const next = await api.get<Session>("/auth/session", { skipAuthRetry: true });
      setSession(next);
      return next;
    } catch {
      setSession(null);
      return null;
    }
  }, []);

  const refreshSession = useCallback(async (): Promise<Session | null> => {
    // Goes through the client's shared attempt: a second concurrent refresh
    // would replay an already-rotated token and revoke the whole family.
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      setAccessToken(null);
      setSession(null);
      return null;
    }
    // A tab that has traded the cookie for a session holds that session: a
    // reload may pick it up again whether or not the device is remembered.
    // And whoever caused the trade, by opening the browser, registering or
    // sending their profile in, is here now: the quiet-spell clock starts
    // from this moment, not from whenever this device was last used.
    markTab();
    recordActivity();
    return loadSession();
  }, [loadSession]);

  // On boot, trade the refresh cookie for an access token so a reload keeps
  // the user signed in. This is the one place the app talks to the auth server
  // before anything is rendered, and every state change here happens after an
  // await, so the lint rule's cascading-render concern does not apply.
  //
  // Only a tab that was signed in, or any tab on a device somebody asked to
  // be remembered on, may do that outright. A fresh tab first asks the other
  // tabs whether one of them is signed in: a link opened in a new tab takes
  // up the session it came from. A fresh tab on a shared tablet, with nobody
  // to answer, may well find a cookie the last person left behind; it is
  // cleared, through the same route as signing out, and the sign-in page is
  // shown instead.
  useEffect(() => {
    const discard = () =>
      api.post("/auth/logout", undefined, { skipAuthRetry: true }).catch(() => undefined);
    const adoptOrDiscard = () =>
      askOtherTabs().then((alive) => (alive ? refreshSession() : discard()));
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void (mayRestoreSession() ? refreshSession() : adoptOrDiscard()).finally(() =>
      setInitialising(false),
    );
  }, [refreshSession]);

  // While this tab holds a session it vouches for it to any new tab that
  // asks, and stops the moment it does not.
  const signedIn = session !== null;
  useEffect(() => (signedIn ? answerOtherTabs() : undefined), [signedIn]);

  // The client calls this when a refresh fails mid-session.
  useEffect(() => {
    onAuthenticationLost(() => setSession(null));
  }, []);

  const signIn = useCallback(
    async (email: string, password: string, rememberMe = false): Promise<Session> => {
      const tokens = await api.post<TokenPair>(
        "/auth/login",
        { email, password, remember_me: rememberMe },
        { skipAuthRetry: true },
      );
      setAccessToken(tokens.access_token);
      rememberDevice(rememberMe);
      markTab();
      recordActivity();
      const next = await api.get<Session>("/auth/session", { skipAuthRetry: true });
      setSession(next);
      return next;
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await api.post("/auth/logout", undefined, { skipAuthRetry: true });
    } finally {
      setAccessToken(null);
      setSession(null);
      forgetSignIn();
      // Portraits and ID scans are held as object URLs for the life of the
      // tab. They belong to whoever just signed out, so they go with them.
      clearImageCache();
    }
  }, []);

  const value = useMemo(
    () => ({ session, initialising, signIn, signOut, refreshSession }),
    [session, initialising, signIn, signOut, refreshSession],
  );

  return (
    <AuthContext value={value}>
      {children}
      {session ? <IdleSignOut signOut={signOut} /> : null}
    </AuthContext>
  );
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
